import Papa from 'papaparse';
import { InviteWithGuests } from '../../types';

export type RsvpFilter = 'all' | 'yes' | 'no' | 'pending' | 'responded';
export type PresenceFilter = 'all' | 'has' | 'none';

export interface GuestCsvFilters {
  email: PresenceFilter;
  rsvp: RsvpFilter;
  dietary: PresenceFilter;
  otherInfo: PresenceFilter;
}

export interface GuestCsvRow {
  'Guest Name': string;
  'RSVP Status': 'Attending' | 'Declined' | 'Pending';
  'Invite Title': string;
  'Invite Code': string;
  Email: string;
  'Dietary Requirements': string;
  'Song Request': string;
  'Other Information': string;
}

const CSV_COLUMNS: Array<keyof GuestCsvRow> = [
  'Guest Name',
  'RSVP Status',
  'Invite Title',
  'Invite Code',
  'Email',
  'Dietary Requirements',
  'Song Request',
  'Other Information',
];

const guestMatchesRsvpFilter = (
  rsvp: boolean | null | undefined,
  filter: RsvpFilter
): boolean => {
  if (filter === 'yes') return rsvp === true;
  if (filter === 'no') return rsvp === false;
  if (filter === 'pending') return rsvp === null || rsvp === undefined;
  if (filter === 'responded') return rsvp === true || rsvp === false;
  return true;
};

const formatRsvpStatus = (
  rsvp: boolean | null | undefined
): GuestCsvRow['RSVP Status'] => {
  if (rsvp === true) return 'Attending';
  if (rsvp === false) return 'Declined';
  return 'Pending';
};

export const buildGuestCsvRows = (
  invites: InviteWithGuests[],
  rsvpFilter: RsvpFilter
): GuestCsvRow[] => invites.flatMap((invite) =>
  invite.guests
    .filter((guest) => guestMatchesRsvpFilter(guest.rsvp, rsvpFilter))
    .map((guest) => ({
      'Guest Name': guest.name,
      'RSVP Status': formatRsvpStatus(guest.rsvp),
      'Invite Title': invite.title,
      'Invite Code': invite.shortcode,
      Email: invite.email || '',
      'Dietary Requirements': invite.dietary || '',
      'Song Request': invite.songRequest || '',
      'Other Information': invite.otherInfo || '',
    }))
);

export const createGuestCsv = (rows: GuestCsvRow[]): string => Papa.unparse(rows, {
  columns: CSV_COLUMNS,
  header: true,
  escapeFormulae: true,
});

const slugify = (value: string): string => value
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const pad = (value: number): string => String(value).padStart(2, '0');

const formatLocalDateTime = (date: Date): string => [
  date.getFullYear(),
  pad(date.getMonth() + 1),
  pad(date.getDate()),
].join('-') + '-' + [
  pad(date.getHours()),
  pad(date.getMinutes()),
  pad(date.getSeconds()),
].join('');

export const buildGuestCsvFilename = (
  eventTitle: string,
  filters: GuestCsvFilters,
  date: Date = new Date()
): string => {
  const filterSlugs: string[] = [];

  if (filters.email !== 'all') filterSlugs.push(`email-${filters.email}`);
  if (filters.rsvp !== 'all') {
    const rsvpLabels: Record<Exclude<RsvpFilter, 'all'>, string> = {
      yes: 'attending',
      no: 'declined',
      pending: 'pending',
      responded: 'responded',
    };
    filterSlugs.push(`rsvp-${rsvpLabels[filters.rsvp]}`);
  }
  if (filters.dietary !== 'all') filterSlugs.push(`dietary-${filters.dietary}`);
  if (filters.otherInfo !== 'all') filterSlugs.push(`other-info-${filters.otherInfo}`);

  const parts = [
    formatLocalDateTime(date),
    slugify(eventTitle) || 'event',
    ...filterSlugs,
    'guests',
  ];

  return `${parts.join('-')}.csv`;
};

export const downloadGuestCsv = (csv: string, filename: string): void => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
